import { formatNumberWithCommas } from 'lib/utils';
import classNames from 'classnames';
import { useMemo } from 'react';
import styles from './styles';

interface ListProps {
  options: Array<any>;
  className: string;
  onClick: any;
  ariaLabel: string;
  dataTestId: string;
  labelExtractor: (option: any) => string;
  valueExtractor: (option: any) => string;
  isSelected: (option: any) => boolean;
  facetCounts?: Record<string, number>;
}

export default function List({
  options,
  className,
  onClick,
  ariaLabel,
  dataTestId,
  labelExtractor,
  valueExtractor,
  isSelected,
  facetCounts,
}: ListProps): JSX.Element {
  function handleKeyDown({
    event,
    val,
    isDisabled,
  }: {
    event: React.KeyboardEvent;
    val: string;
    isDisabled: boolean;
  }): void {
    if (isDisabled) {
      event.stopPropagation();
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      onClick(val);
    }
  }

  function handleClick({
    val,
    isDisabled,
    event,
  }: {
    val: string;
    isDisabled: boolean;
    event: React.MouseEvent;
  }): void {
    if (isDisabled) {
      event.stopPropagation();
      return;
    }

    onClick(val);
  }

  const processedOptions = useMemo(
    () =>
      options.map((option) => ({
        option,
        label: labelExtractor(option),
        value: valueExtractor(option),
        facetCount: facetCounts?.[valueExtractor(option)],
        isAnyCategory: option === 'Any Category',
        isDisabled:
          facetCounts?.[valueExtractor(option)] === 0 ||
          (facetCounts?.[valueExtractor(option)] === undefined && option !== 'Any Category'),
        isSelected: isSelected(option),
      })),
    [options, labelExtractor, valueExtractor, facetCounts, isSelected]
  );

  return (
    <>
      <ul role="listbox" className={className} aria-label={ariaLabel} data-testid={dataTestId}>
        {processedOptions.map(
          (
            { label, value, facetCount, isAnyCategory, isDisabled, isSelected: selected },
            index
          ) => {
            return (
              <li
                key={index}
                className={classNames('list-item', {
                  selected,
                  'zero-facets': isDisabled,
                })}
                onClick={(event): void => handleClick({ val: value, isDisabled, event })}
                onKeyDown={(event): void => handleKeyDown({ event, val: value, isDisabled })}
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                tabIndex={0}
                role="option"
                aria-selected={selected}
              >
                {selected && <div className="selected-icon" />}
                {label}
                {!isAnyCategory && (
                  <span
                    style={{ paddingLeft: '4px' }}
                    className={classNames({ 'zero-facets': isDisabled })}
                  >{`(${facetCount ? formatNumberWithCommas(facetCount as number) : 0})`}</span>
                )}
              </li>
            );
          }
        )}
      </ul>
      <style>{styles}</style>
    </>
  );
}
